import { Pressable, Text, View } from "react-native";

import { type Kind, KINDS } from "@/lib/inventory/kind";
import { cn } from "@/lib/ui/cn";
import { spaceActivates } from "@/lib/ui/webKeyboard";

type Props =
  & (
    /** An add flow: one kind is always chosen. */
    | { value: Kind; onChange: (kind: Kind) => void; anyLabel?: undefined; }
    /** A filter: `anyLabel` leads the tabs and stands for no constraint (`null`). */
    | { value: Kind | null; onChange: (kind: Kind | null) => void; anyLabel: string; }
  )
  & {
    /** Prefixes each tab's test id: `<testID>-raw`, `-graded`, `-sealed`, and `-any` for "All". */
    testID?: string;
    compact?: boolean;
    /**
     * The kinds offered, in `KINDS` order. A step that answers only part of the split names its
     * part: hand-add's catalog search offers Raw and Sealed, its confirm step Raw and Graded.
     */
    kinds?: readonly Kind[];
    /** Overrides a kind's tab text where the step reads better another way ("Cards" for Raw). */
    labels?: Partial<Record<Kind, string>>;
  };

/**
 * The Raw / Graded / Sealed tabs (`lib/inventory/kind`), shared by every screen that offers the
 * split: hand-add, the inventory filter and the storefront browse filter (TRO-1101). Adopt this
 * rather than listing the kinds again, so the three cannot drift apart.
 *
 * `role="tab"` because the kinds are views of one list or flow, not settings. Pill pattern from
 * `BookMoneyHeadline`.
 */
export function KindSwitch(props: Props) {
  const offered = KINDS
    .filter(({ kind }) => !props.kinds || props.kinds.includes(kind))
    .map(({ kind, label }) => ({ kind, label: props.labels?.[kind] ?? label }));
  const tabs: { kind: Kind | null; label: string; }[] = props.anyLabel === undefined
    ? offered
    : [{ kind: null, label: props.anyLabel }, ...offered];
  return (
    <View
      accessibilityRole="tablist"
      className={cn(
        "flex-row items-center self-start border border-border",
        props.compact ? "h-11 rounded-lg bg-surface p-1" : "rounded-full bg-surface-muted p-1",
      )}
    >
      {tabs.map(({ kind, label }) => {
        const on = kind === props.value;
        const choose = () => {
          if (props.anyLabel === undefined) {
            if (kind !== null) props.onChange(kind);
          } else {
            props.onChange(kind);
          }
        };
        return (
          <Pressable
            key={label}
            testID={`${props.testID ?? "kind"}-${kind ?? "any"}`}
            onPress={choose}
            hitSlop={props.compact ? { top: 5, bottom: 5 } : undefined}
            {...spaceActivates(choose)}
            accessibilityRole="tab"
            // Set both: react-native-web drops `accessibilityState` (mobile/AGENTS.md).
            aria-selected={on}
            accessibilityState={{ selected: on }}
            className={cn(
              "items-center justify-center",
              props.compact ? "h-[34px] rounded-[9px] px-2" : "h-11 rounded-full px-5",
              on && "bg-accent-soft",
            )}
          >
            <Text
              className={cn(
                "font-label",
                props.compact ? "text-xs" : "text-[13px]",
                on ? "font-semibold text-primary" : "text-muted-foreground",
              )}
            >
              {label}
            </Text>
          </Pressable>
        );
      })}
    </View>
  );
}
